import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  ShieldCheck,
  Hash,
  Clock4,
  GraduationCap,
  BookOpen,
  AlertTriangle,
  Loader2,
  CheckCircle2,
  Cpu,
  Lock,
  ChevronRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VerifyReceiptPage() {
  const { hash } = useParams();
  const [state, setState] = useState("loading"); // loading | ok | not_found
  const [receipt, setReceipt] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/verify/${hash}`);
        setReceipt(data);
        setState("ok");
      } catch (e) {
        setError(e?.response?.data?.detail || "No pudimos verificar este sello.");
        setState("not_found");
      }
    })();
  }, [hash]);

  return (
    <div className="min-h-screen bg-paper text-ink">
      {/* Header */}
      <div className="border-b border-rule bg-paper/85 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            data-testid="verify-brand"
            className="inline-flex items-baseline gap-0.5"
          >
            <span className="font-serif italic text-2xl text-copper">una</span><span className="font-serif text-2xl text-ink">TESIS</span>
          </Link>
          <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium hidden sm:block">
            Notario Digital · Verificación pública
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 lg:px-8 py-10 lg:py-14" data-testid="verify-page">
        {state === "loading" && (
          <div className="flex items-center gap-2 text-ink-soft text-[13px]" data-testid="verify-loading">
            <Loader2 size={14} className="animate-spin" /> Verificando huella en el ledger…
          </div>
        )}

        {state === "not_found" && (
          <NotFound hash={hash} error={error} />
        )}

        {state === "ok" && receipt && <Receipt receipt={receipt} hash={hash} />}
      </div>
    </div>
  );
}

function Receipt({ receipt, hash }) {
  const ts = receipt.timestamp ? new Date(receipt.timestamp) : null;
  const dateLabel = ts
    ? ts.toLocaleString("es-MX", {
        dateStyle: "full",
        timeStyle: "long",
      })
    : "—";

  return (
    <div data-testid="verify-receipt">
      {/* Hero */}
      <div className="rounded-3xl bg-[#1d2c22] text-paper p-8 sm:p-10 relative overflow-hidden ring-1 ring-white/5">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.06] bg-[linear-gradient(rgba(255,255,255,0.6)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.6)_1px,transparent_1px)] bg-[size:36px_36px]"
        />
        <div className="relative flex flex-col sm:flex-row sm:items-center gap-6">
          <span className="grid place-items-center h-16 w-16 shrink-0 rounded-full bg-[#E0C58A] text-[#1d2c22] shadow-[0_18px_36px_-12px_rgba(224,197,138,0.55)]">
            <ShieldCheck size={22} />
          </span>
          <div className="flex-1">
            <p className="text-[10px] tracking-[0.28em] uppercase text-[#E0C58A] font-medium">
              Sello Verificado · {receipt.status}
            </p>
            <h1 className="mt-2 font-serif text-3xl sm:text-4xl text-paper leading-tight text-balance">
              Este manuscrito tiene{" "}
              <span className="italic text-[#E0C58A]">prueba criptográfica</span>{" "}
              de autoría.
            </h1>
            <p className="mt-3 text-[14px] text-paper/75 max-w-2xl">
              El siguiente registro fue anclado en el historial inmutable de unaTESIS.
              Nadie puede modificar, antedatar ni borrar esta entrada sin destruir la cadena completa.
            </p>
          </div>
        </div>
      </div>

      {/* Author + thesis card */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <InfoCard
          icon={GraduationCap}
          label="Autor original"
          value={receipt.author_name}
          subvalue={receipt.author_email_masked}
          testid="verify-author"
        />
        <InfoCard
          icon={BookOpen}
          label="Tesis"
          value={receipt.thesis_title || "—"}
          subvalue={receipt.career}
          testid="verify-thesis"
        />
        <InfoCard
          icon={Lock}
          label="Universidad"
          value={receipt.university || "—"}
          subvalue="Institución declarada"
          testid="verify-university"
        />
      </div>

      {/* Timestamp + chain */}
      <div className="mt-4 rounded-2xl border border-rule bg-white p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium inline-flex items-center gap-1.5">
              <Clock4 size={11} /> Fecha y hora del sello
            </p>
            <p
              data-testid="verify-timestamp"
              className="mt-2 font-serif text-xl text-ink leading-snug"
            >
              {dateLabel}
            </p>
            <p className="mt-1 text-[11.5px] text-ink-soft">
              Zona horaria del visitante. UTC original anclado en el ledger.
            </p>
          </div>
          <div>
            <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium inline-flex items-center gap-1.5">
              <Cpu size={11} /> Posición en la cadena
            </p>
            <p className="mt-2 font-serif text-xl text-ink leading-snug">
              Bloque {receipt.chain_index}{" "}
              <span className="text-ink-soft">/ {receipt.project_chain_length}</span>
            </p>
            <p className="mt-1 text-[11.5px] text-ink-soft">
              {receipt.is_genesis
                ? "Este es el primer sello del proyecto (Génesis)."
                : "Encadenado con el bloque anterior."}
            </p>
          </div>
        </div>
      </div>

      {/* Hashes */}
      <div className="mt-4 rounded-2xl border border-rule bg-[#15201a] text-paper p-6 font-mono text-[12px] leading-relaxed">
        <p className="text-[10px] tracking-[0.22em] uppercase text-[#E0C58A] mb-3 inline-flex items-center gap-1.5">
          <Hash size={11} /> Huella criptográfica · SHA-256
        </p>
        <p
          data-testid="verify-hash"
          className="text-[#E0C58A] break-all leading-tight"
        >
          {receipt.text_snapshot_hash}
        </p>
        <p className="mt-3 text-[10px] tracking-[0.22em] uppercase text-paper/50">
          Hash del bloque anterior
        </p>
        <p
          data-testid="verify-prev-hash"
          className="text-paper/85 break-all leading-tight"
        >
          {receipt.previous_block_hash}
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-2 text-[10.5px]">
          <Tag icon={CheckCircle2} label="Sello verificado" />
          <Tag icon={Lock} label={`Snapshot de ${receipt.snapshot_length.toLocaleString("es-MX")} caracteres`} />
          <Tag icon={Hash} label={receipt.chapter_name} />
        </div>
      </div>

      {/* Footer / legal */}
      <div className="mt-6 rounded-2xl border border-copper/30 bg-[#FBF6EC] p-5 flex flex-col sm:flex-row gap-3 items-start">
        <span className="grid place-items-center h-8 w-8 shrink-0 rounded-full bg-forest text-paper">
          <ShieldCheck size={14} className="text-[#E0C58A]" />
        </span>
        <div className="flex-1">
          <p className="font-serif text-lg text-ink leading-snug">
            ¿Qué significa esto si soy asesor, sinodal o jurado?
          </p>
          <p className="mt-1.5 text-[13px] text-ink-soft leading-relaxed">
            Que el manuscrito asociado a esta huella SHA-256 ya existía bajo la autoría de{" "}
            <span className="text-ink font-medium">{receipt.author_name}</span>{" "}
            a las <span className="text-ink font-medium">{dateLabel}</span>. Si alguien
            te presenta una versión idéntica o muy parecida con fecha posterior,
            tienes una prueba pública —matemática e histórica— de quién la escribió primero.
          </p>
        </div>
      </div>

      <p className="mt-8 text-center text-[11px] text-ink-soft">
        Hash consultado:{" "}
        <code className="font-mono text-ink-soft/80">{hash}</code>
      </p>
    </div>
  );
}

function InfoCard({ icon: Icon, label, value, subvalue, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border border-rule bg-white p-5 flex flex-col"
    >
      <span className="grid place-items-center h-9 w-9 rounded-lg bg-forest/10 text-forest">
        <Icon size={14} />
      </span>
      <p className="mt-3 text-[10px] tracking-[0.22em] uppercase text-copper font-medium">
        {label}
      </p>
      <p className="mt-1 font-serif text-[17px] text-ink leading-snug">
        {value || "—"}
      </p>
      {subvalue && (
        <p className="mt-1 text-[11.5px] text-ink-soft truncate">{subvalue}</p>
      )}
    </div>
  );
}

function Tag({ icon: Icon, label }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-white/5 ring-1 ring-white/10 px-2.5 py-1 text-paper/85">
      <Icon size={10} className="text-[#E0C58A]" />
      {label}
    </span>
  );
}

function NotFound({ hash, error }) {
  return (
    <div className="rounded-2xl border border-[#B6533F]/30 bg-[#FBEFEA] p-8 text-center" data-testid="verify-not-found">
      <span className="mx-auto grid place-items-center h-12 w-12 rounded-full bg-[#B6533F] text-paper">
        <AlertTriangle size={18} />
      </span>
      <h1 className="mt-4 font-serif text-2xl text-ink">Huella no encontrada</h1>
      <p className="mt-2 text-[13px] text-ink-soft max-w-md mx-auto">
        {error}
      </p>
      <p className="mt-4 text-[11.5px] text-ink-soft">
        Hash consultado:{" "}
        <code className="font-mono">{hash}</code>
      </p>
      <Link
        to="/"
        className="mt-6 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-[13px] font-medium text-paper bg-forest hover:bg-forest-dark"
      >
        Volver al inicio
        <ChevronRight size={13} />
      </Link>
    </div>
  );
}
